import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import TrademarkImagePreview from "./TrademarkImagePreview";

const RESULT_ICON_MAP = {
  description: DescriptionOutlinedIcon,
  shield: GppGoodOutlinedIcon,
  policy: PolicyOutlinedIcon,
  award: WorkspacePremiumOutlinedIcon,
  verified: VerifiedUserOutlinedIcon,
};

const getPTOLink = (jurisdiction, applicationNo) => {
  // Extract numeric application number if it contains country code
  const appNum = applicationNo.replace(/^[A-Z]+-/, "").trim();

  switch (jurisdiction.toLowerCase()) {
    case "united states":
    case "us":
      return `https://tmsearch.uspto.gov/bean/servlet/Search?sls=on&stracs=on&netaband=on&tvx=nav-bar-null.html&strtQry=${appNum}`;
    case "european union":
    case "eu":
      return `https://euipo.europa.eu/eSearch/#advanced/trademarks?applicationNumber=${appNum}`;
    case "australia":
    case "au":
      return `https://search.ipaustralia.gov.au/trademarks/search/quick?q=${appNum}`;
    default:
      return null;
  }
};

const TrademarkResultCard = ({ result, isChecked, onToggle, onViewDetail }) => {
  const ResultIcon = RESULT_ICON_MAP[result.iconType] || DescriptionOutlinedIcon;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: "16px",
        border: "1px solid #d9e3ef",
        backgroundColor: "#ffffff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mb: 1.5 }}>
            <Checkbox checked={isChecked} onChange={onToggle} sx={{ mt: -0.75 }} />
            <Box>
              <Typography
                role="button"
                tabIndex={0}
                onClick={onViewDetail}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onViewDetail();
                  }
                }}
                sx={{
                  color: "#1d70c9",
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  textDecoration: "underline",
                  textUnderlineOffset: "3px",
                  mb: 0.5,
                  cursor: "pointer",
                }}
              >
                {result.markName}
              </Typography>
              <Typography sx={{ color: "#475569", fontSize: "0.95rem" }}>
                {result.jurisdiction} | {result.legalStatus}
              </Typography>
              <Typography sx={{ color: "#7a8ca5", fontSize: "0.95rem", mt: 0.25 }}>
                Application No: {result.applicationNo}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ color: "#7a8ca5", fontSize: "0.95rem", mb: 0.25 }}>
              International Classes
            </Typography>
            <Typography sx={{ color: "#13233f", fontWeight: 700, fontSize: "1rem" }}>
              {result.classes.join(", ")}
            </Typography>
          </Box>

          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ color: "#7a8ca5", fontSize: "0.95rem", mb: 0.25 }}>
              Goods & Description
            </Typography>
            <Typography
              sx={{
                color: "#13233f",
                fontWeight: 600,
                fontSize: "1rem",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {result.goodsDescription}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ color: "#7a8ca5", fontSize: "0.95rem", mb: 0.25 }}>
                Application Date
              </Typography>
              <Typography sx={{ color: "#13233f", fontWeight: 700, fontSize: "1rem" }}>
                {result.applicationDate}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ color: "#7a8ca5", fontSize: "0.95rem", mb: 0.25 }}>
                Registration Date
              </Typography>
              <Typography sx={{ color: "#13233f", fontWeight: 700, fontSize: "1rem" }}>
                {result.registrationDate}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ color: "#7a8ca5", fontSize: "0.95rem", mb: 0.25 }}>
                Renewal Date
              </Typography>
              <Typography sx={{ color: "#13233f", fontWeight: 700, fontSize: "1rem" }}>
                {result.renewalDate}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            ml: "auto",
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          {/* <Chip
            label={`MATCH SCORE ${result.matchScore}`}
            sx={{
              height: "auto",
              borderRadius: "999px",
              backgroundColor: "#ebf8ee",
              color: "#18794e",
              px: 1,
              py: 1.25,
              fontSize: "0.95rem",
              fontWeight: 700,
              "& .MuiChip-label": {
                display: "block",
                whiteSpace: "normal",
                textAlign: "center",
                lineHeight: 1.35,
                padding: "10px 14px",
              },
            }}
          /> */}

          <TrademarkImagePreview
            applicationNum = {result.applicationNo}
            trademarkImage={result.trademarkImage}
            alt={result.markName}
            fallback={<ResultIcon sx={{ fontSize: 66, color: "#2b73c6" }} />}
          />

          {(
            <Button
              variant="contained"
              href={result.ptoLink}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<LaunchRoundedIcon />}
              sx={{
                background: "linear-gradient(135deg, #1d70c9 0%, #174ea0 100%)",
                color: "#ffffff",
                textTransform: "none",
                px: 2.75,
                py: 1.25,
                borderRadius: "12px",
                boxShadow: "0 14px 30px rgba(29, 112, 201, 0.22)",
                "&:hover": {
                  background: "linear-gradient(135deg, #195fa9 0%, #143f82 100%)",
                  boxShadow: "0 16px 34px rgba(29, 112, 201, 0.28)",
                },
              }}
            >
              View in PTO
            </Button>
          )} 

          <Button
            variant="outlined"
            onClick={onViewDetail}
            sx={{
              color: "#1d70c9",
              borderColor: "#bfd0e3",
              textTransform: "none",
              px: 2.5,
            }}
          >
            View Detail
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default TrademarkResultCard;
